import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { formatMoney } from '@/features/workspace-usage';
import type { OrganizationUsageSummary } from '@/lib/api/generated/model';

import { LIMITS_POLICY_GRID } from '../adminLayout';
import { useClearConcurrencyLimit } from '../hooks/useConcurrency';
import type { LimitsPolicy } from '../workspaceLimits';
import { RunsDefaultEditor, SpendDefaultEditor } from './DefaultEditors';

export interface LimitsPolicyCardsProps {
  policy: LimitsPolicy;
  /** The spend read, which the spend default's stop forecast is computed from. */
  summary: OrganizationUsageSummary;
  /** Whether the service reports run capacity at all (R9). */
  hasRuns: boolean;
}

const CARD =
  'border-border bg-card flex min-w-0 flex-col gap-1 rounded-lg border px-4 py-3';

function follows(count: number): string {
  return `${String(count)} follow it`;
}

/**
 * The rules that apply to every workspace (spec 117 US1, US4): spend per workspace, runs
 * at once per workspace, and the installation's ceiling. Side by side at the top of the
 * page, where the two defaults used to sit half a page apart; each editable one opens
 * its editor beneath the row, one at a time. The ceiling is configuration and offers no
 * control.
 */
export function LimitsPolicyCards({
  policy,
  summary,
  hasRuns,
}: LimitsPolicyCardsProps) {
  const [editing, setEditing] = useState<'spend' | 'runs' | null>(null);
  const clearRuns = useClearConcurrencyLimit(policy.ceiling ?? 1);
  const currency = summary.spend.currency ?? 'USD';

  return (
    <>
      <section aria-label="Organization rules" className={LIMITS_POLICY_GRID}>
        <div className={CARD}>
          <span className="text-muted-foreground text-xs">
            Spend per workspace
          </span>
          <span className="text-foreground text-xl font-bold tabular-nums">
            {policy.spendDefault === null
              ? 'Not set — no limit'
              : formatMoney(policy.spendDefault, currency)}
          </span>
          <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
            <span>{follows(policy.followingSpend)}</span>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Edit spend default"
              aria-expanded={editing === 'spend'}
              onClick={() => {
                setEditing(editing === 'spend' ? null : 'spend');
              }}
            >
              Edit
            </Button>
          </div>
        </div>

        {hasRuns ? (
          <div className={CARD}>
            <span className="text-muted-foreground text-xs">
              Runs at once per workspace
            </span>
            <span className="text-foreground text-xl font-bold tabular-nums">
              {policy.runsDefault === null
                ? `Not set — ${String(policy.fallback)} per workspace`
                : String(policy.runsDefault)}
            </span>
            <div className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
              <span>{follows(policy.followingRuns)}</span>
              <span className="flex gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={
                    policy.runsDefault === null
                      ? 'Set runs default'
                      : 'Change runs default'
                  }
                  aria-expanded={editing === 'runs'}
                  onClick={() => {
                    setEditing(editing === 'runs' ? null : 'runs');
                  }}
                >
                  {policy.runsDefault === null ? 'Set' : 'Change'}
                </Button>
                {policy.runsDefault === null ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Remove runs default"
                    disabled={clearRuns.isPending}
                    onClick={() => {
                      clearRuns.mutate({ workspaceId: null });
                    }}
                  >
                    Remove
                  </Button>
                )}
              </span>
            </div>
            {clearRuns.isError ? (
              <p role="alert" className="text-destructive text-xs">
                {clearRuns.error.message}
              </p>
            ) : null}
          </div>
        ) : null}

        {hasRuns ? (
          <div className={CARD}>
            <span className="text-muted-foreground text-xs">
              Installation ceiling
            </span>
            <span className="text-foreground text-xl font-bold tabular-nums">
              {`${String(policy.ceiling)} runs at once`}
            </span>
            <span className="text-muted-foreground text-xs">
              Set in configuration
            </span>
          </div>
        ) : null}
      </section>

      {editing === 'spend' ? (
        <SpendDefaultEditor
          summary={summary}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : null}
      {editing === 'runs' &&
      policy.ceiling !== null &&
      policy.fallback !== null ? (
        <RunsDefaultEditor
          current={policy.runsDefault}
          fallback={policy.fallback}
          ceiling={policy.ceiling}
          onDone={() => {
            setEditing(null);
          }}
        />
      ) : null}
    </>
  );
}
